import React, { useLayoutEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { LineChartTooltip } from './LineChartTooltip';
import { isNullOrUndefined } from '../../util';
import { VBox } from '../layout';
import type { DoubleLineData, LineChartProps } from './LineChartUtil';
import {
  d3,
  getChartSvgProps,
  LineChartHeader,
  margin,
  SharedLineChartSetup,
  useChartLayoutEffect,
  useChartRefs,
  useScaledXAxis,
  useShowLoadingMask,
  useTooltipHandler,
  useTrackChartDimensions
} from './LineChartUtil';
import { BlankState, LoadMask } from '../basic';
import { intl } from '../../lib';

export type DoubleLineChartProps = LineChartProps & {
  data: DoubleLineData[];
  firstLineLegend: string;
  secondLineLegend: string;
};

export const DoubleLineChart = ({
  data,
  header,
  headerModalTitle,
  headerModalContent,
  firstLineLegend,
  secondLineLegend,
  initialWidth = 400,
  initialHeight = 250
}: Readonly<DoubleLineChartProps>): JSX.Element => {
  const { legendRef, dotsGroupRef, svgContainerRef } = useChartRefs();
  const { width, height } = useTrackChartDimensions(
    svgContainerRef,
    initialWidth,
    initialHeight
  );
  const isMasked = useShowLoadingMask();

  const x = useScaledXAxis(data, width);

  const y = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([
          (d3.min([
            d3.min(data, d => d.y.firstLine.value) as number,
            d3.min(data, d => d.y.secondLine.value) as number
          ]) as number) * 0.9,
          (d3.max([
            d3.max(data, d => d.y.firstLine.value) as number,
            d3.max(data, d => d.y.secondLine.value) as number
          ]) as number) * 1.1
        ])
        .range([height - margin.bottom, margin.top]),
    [data, height]
  );

  const filteredDataFirstLine = useMemo(
    () =>
      data.filter(
        d => !isNullOrUndefined(d.x) && !isNullOrUndefined(d.y.firstLine.value)
      ),
    [data]
  );
  const filteredDataSecondLine = useMemo(
    () =>
      data.filter(
        d => !isNullOrUndefined(d.x) && !isNullOrUndefined(d.y.secondLine.value)
      ),
    [data]
  );

  const firstLine = useMemo(
    () =>
      d3
        .line<DoubleLineData>()
        .defined(
          d =>
            !isNullOrUndefined(d.x) && !isNullOrUndefined(d.y.firstLine.value)
        )
        .x(d => x(new Date(d.x as string).valueOf()) as number)
        .y(d => y(d.y.firstLine.value as number) as number),
    [x, y]
  );

  const secondLine = useMemo(
    () =>
      d3
        .line<DoubleLineData>()
        .defined(
          d =>
            !isNullOrUndefined(d.x) && !isNullOrUndefined(d.y.secondLine.value)
        )
        .x(d => x(new Date(d.x as string).valueOf()) as number)
        .y(d => y(d.y.secondLine.value as number) as number),
    [x, y]
  );

  useLayoutEffect(() => {
    if (legendRef.current) {
      // because the text can be dynamic, we can't use a static positioning
      // and svg doesn't allow anything similar to a flex, so we have to use this workaround
      // to create part of the legend based on the width of the rendered elements
      const offsetWidth = legendRef.current.getBBox().width;
      d3.select(legendRef.current)
        .append('rect')
        .attr('class', 'legend-rect-second-line')
        .attr('width', 15)
        .attr('height', 4)
        .attr('transform', `translate(${20 + offsetWidth}, -6)`);
      d3.select(legendRef.current)
        .append('text')
        .attr('class', 'legend-text legend-text-second-line')
        .attr('transform', `translate(${40 + offsetWidth}, 0)`)
        .text(secondLineLegend);
    }
  }, [legendRef, secondLineLegend]);

  const { tooltipState, showTooltip, hideTooltip } = useTooltipHandler(
    data,
    dotsGroupRef,
    x,
    y,
    width
  );

  useChartLayoutEffect(dotsGroupRef, showTooltip, hideTooltip);

  return (
    <VBox className="line-chart-container" ref={svgContainerRef}>
      <LoadMask masked={isMasked} />
      <LineChartHeader
        header={header}
        headerModalTitle={headerModalTitle}
        headerModalContent={headerModalContent}
      />
      {data.length === 0 ? (
        <BlankState
          description={intl('common.noData')}
          iconName="database-no"
          scale="small"
        />
      ) : (
        <div className="ehs-chart-svg-container">
          <svg {...getChartSvgProps(width, height)}>
            <SharedLineChartSetup
              data={data}
              x={x}
              y={y}
              height={height}
              width={width}
              firstLineLegend={firstLineLegend}
              legendRef={legendRef}
            />
            <g>
              <path
                transform={`translate(${margin.left}, 0)`}
                className="line first-line"
                d={firstLine(filteredDataFirstLine) ?? undefined}
              />
              <path
                transform={`translate(${margin.left}, 0)`}
                className="line second-line"
                d={secondLine(filteredDataSecondLine) ?? undefined}
              />

              <g ref={dotsGroupRef}>
                {data.map(d => (
                  <React.Fragment key={uuidv4()}>
                    {!isNullOrUndefined(d.x) &&
                      !isNullOrUndefined(d.y.firstLine.value) && (
                        <circle
                          cx={x(new Date(d.x).valueOf())}
                          cy={y(d.y.firstLine.value)}
                          r={5}
                          fill="var(--secondary-color)"
                          transform={`translate(${margin.left}, 0)`}
                        />
                      )}
                    {!isNullOrUndefined(d.x) &&
                      !isNullOrUndefined(d.y.secondLine.value) && (
                        <circle
                          cx={x(new Date(d.x).valueOf())}
                          cy={y(d.y.secondLine.value)}
                          r={5}
                          fill="var(--primary-color)"
                          transform={`translate(${margin.left}, 0)`}
                        />
                      )}
                  </React.Fragment>
                ))}
              </g>
            </g>
            <LineChartTooltip
              {...tooltipState}
              firstLineLegend={firstLineLegend}
              secondLineLegend={secondLineLegend}
              showHoverGuidelines={true}
              width={width}
              height={height}
            />
          </svg>
        </div>
      )}
    </VBox>
  );
};
